/* eslint-disable no-nested-ternary */

import {createSlice, current} from '@reduxjs/toolkit';
import {DEFAULT_PAGINATION} from 'src/hooks/common-data-service/cached-table';
import {BULK_IMPORT_USERS_STEPS} from 'src/modules/create-user/constants';
import {UserStatus as UserStatuses} from 'src/openapi/admin/models/UserStatus';
import {UserType as UserTypes} from 'src/openapi/admin/models/UserType';

import type {PayloadAction} from '@reduxjs/toolkit';
import type {UpdatedData} from 'src/components/with-interaction-table/typedef';
import type {BulkImportUserType, UserBulkImportFileType} from 'src/modules/create-user/user-bulk-import/typedef';
import type {AccountGroup} from 'src/openapi/admin/models/AccountGroup';
import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {ImportOptions} from 'src/openapi/admin/models/ImportOptions';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UserStatus} from 'src/openapi/admin/models/UserStatus';
import type {UserType} from 'src/openapi/admin/models/UserType';
import type {AppState} from 'src/store/typedef';

type InitialState = {
    step: BULK_IMPORT_USERS_STEPS,
    targetAccount: AccountShortInfo | null,
    templateNewsletter: string[] | null,
    importSettings: ImportOptions,
    selectedType: UserType,
    selectedStatus: UserStatus,
    templateData: BulkImportUserType[],
    file: UserBulkImportFileType[] | null,
    validateStepPagination: Pagination,

    updatedData: UpdatedData[],
};

export const initialState: InitialState = {
    step: BULK_IMPORT_USERS_STEPS.UPLOAD_FILE,
    templateNewsletter: null,
    targetAccount: null,
    templateData: [],
    selectedType: UserTypes.T_LC,
    selectedStatus: UserStatuses.ENABLED,
    validateStepPagination: DEFAULT_PAGINATION,
    importSettings: {
        replaceSearches: false,
        replaceAlerts: false,
        replaceLayouts: false,
    },
    file: null,

    updatedData: [],
};

export const userBulkImportSlice = createSlice({
    name: 'userBulkImport',
    initialState,
    reducers: {
        setValidateFileStep: state => {
            state.step = BULK_IMPORT_USERS_STEPS.VALIDATE_FILE;
        },
        setTemplateData: (state, action) => {
            state.templateData = action.payload;
        },
        setFile: (state, action) => {
            state.file = action.payload;
        },
        setImportSettings: (state, action) => {
            state.importSettings = action.payload;
        },
        setUserType: (state, action) => {
            state.selectedType = action.payload;
        },
        setUserStatus: (state, action) => {
            state.selectedStatus = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.validateStepPagination.currentPage = action.payload;
        },
        setPageSize: (state, action) => {
            state.validateStepPagination = {
                currentPage: 1,
                pageSize: action.payload,
            };
        },
        setTargetAccount: (state, action) => {
            state.targetAccount = action.payload;
        },
        setTemplateNewsletter: (state, action) => {
            state.templateNewsletter = action.payload;
        },
        updateColumnByIds: (state, action: PayloadAction<UpdatedData[]>) => {
            const previousUpdatedData = current(state).updatedData;
            const updatedData = [...action.payload];

            updatedData.map(row => {
                current(state).templateData.forEach(({id}, index) => {
                    if (id === row.rowId) {
                        row.rowId = index;
                    }
                });

                return row;
            });

            state.updatedData = [
                ...previousUpdatedData.filter(({rowId, columnId}) => !updatedData
                    .some(updatedRow => updatedRow.rowId === rowId && updatedRow.columnId === columnId)),
                ...updatedData,
            ];
        },
        updateColumnByRowIndexes: (state, action: PayloadAction<UpdatedData[]>) => {
            const previousUpdatedData = current(state).updatedData;
            const pagination = current(state).validateStepPagination;
            const paginatedUpdatedData = action.payload.map(({rowId, ...otherFields}) => ({
                ...otherFields,
                rowId: pagination.pageSize * (pagination.currentPage - 1) + rowId,
            }));

            state.updatedData = [
                ...previousUpdatedData.filter(({rowId, columnId}) => !paginatedUpdatedData
                    .some(updatedRow => updatedRow.rowId === rowId && updatedRow.columnId === columnId)),
                ...paginatedUpdatedData,
            ];
        },
        removeRecords: (state, action: PayloadAction<number[]>) => {
            const remainedUsers = current(state).templateData.filter(({id}) => !action.payload.includes(id));
            state.templateData = remainedUsers;
            const {currentPage, pageSize} = current(state).validateStepPagination;

            const possiblePage = Math.ceil(remainedUsers.length / pageSize);
            if (currentPage > possiblePage) {
                state.validateStepPagination.currentPage = possiblePage;
            }
        },
        setType: (state, action: PayloadAction<{ids: number[], userType: UserType}>) => {
            const {ids, userType} = action.payload;
            state.templateData = current(state).templateData.map(({id, ...other}) => (ids.includes(id)
                ? {
                        id,
                        ...other,
                        userType,
                        userStatus: userType === UserTypes.T_NA
                            ? UserStatuses.EMPTY
                            : other.userStatus === UserStatuses.EMPTY
                                ? UserStatuses.DISABLED
                                : other.userStatus,
                    } : {id, ...other}
            ));
        },
        setStatus: (state, action: PayloadAction<{ids: number[], userStatus: UserStatus}>) => {
            const {ids, userStatus} = action.payload;
            state.templateData = current(state).templateData.map(({id, ...other}) => (ids.includes(id)
                ? {
                        id,
                        ...other,
                        userStatus,
                        userType: userStatus === UserStatuses.EMPTY
                            ? UserTypes.T_NA
                            : other.userType === UserTypes.T_NA
                                ? UserTypes.T_LC
                                : other.userType,
                    } : {id, ...other}
            ));
        },
        setGroup: (state, action: PayloadAction<{ids: number[], group: AccountGroup | null}>) => {
            const {ids, group} = action.payload;
            state.templateData = current(state).templateData.map(({id, ...other}) => (ids.includes(id)
                ? {id, ...other, group}
                : {id, ...other}
            ));
        },
        setCountry: (state, action: PayloadAction<{ids: number[], country: DictionaryItem}>) => {
            const {ids, country} = action.payload;
            state.templateData = current(state).templateData.map(({id, ...other}) => (ids.includes(id)
                ? {id, ...other, country}
                : {id, ...other}
            ));
        },
        setNewsletters: (state, action: PayloadAction<{ids: number[], newsletters: string[]}>) => {
            const {ids, newsletters} = action.payload;
            state.templateData = current(state).templateData.map(({id, ...other}) => (ids.includes(id)
                ? {id, ...other, newsletters}
                : {id, ...other}
            ));
        },
        applyUpdatedData: state => {
            const updatedData = current(state).updatedData;
            const templateBuffer = [...current(state).templateData];

            updatedData.forEach(({rowId, columnId, value, validationError}: UpdatedData) => {
                const itemBuffer = templateBuffer[rowId];
                if (itemBuffer) {
                    templateBuffer[rowId] = {
                        ...itemBuffer,
                        [columnId]: {value, validationError},
                    };
                }
            });

            state.templateData = templateBuffer.filter(Boolean);
            state.updatedData = [];
        },
        clearUsersBulkImport: state => {
            state.step = BULK_IMPORT_USERS_STEPS.UPLOAD_FILE;
            state.targetAccount = null;
            state.validateStepPagination = DEFAULT_PAGINATION;
            state.templateData = [];
            state.updatedData = [];
            state.selectedType = UserTypes.T_LC;
            state.selectedStatus = UserStatuses.ENABLED;
            state.importSettings = {
                replaceSearches: false,
                replaceAlerts: false,
                replaceLayouts: false,
            };
            state.file = null;
        },
    },
});

export const selectBulkUserImportStep = (state: AppState) => state.createUsers.bulkImport.step;
export const selectBulkUserImportTargetAccount = (state: AppState) => state.createUsers.bulkImport.targetAccount;
export const selectBulkUserImportTemplateData = (state: AppState) => state.createUsers.bulkImport.templateData;
export const selectBulkUserImportFile = (state: AppState) => state.createUsers.bulkImport.file;
export const selectBulkUserImportImportSettings = (state: AppState) => state.createUsers.bulkImport.importSettings;
export const selectBulkUserImportSelectedType = (state: AppState) => state.createUsers.bulkImport.selectedType;
export const selectBulkUserImportSelectedStatus = (state: AppState) => state.createUsers.bulkImport.selectedStatus;
export const selectBulkUserImportTemplateNewsletter = (state: AppState) =>
    state.createUsers.bulkImport.templateNewsletter;
export const selectBulkUserImportUpdatedData = (state: AppState) => state.createUsers.bulkImport.updatedData;
export const selectBulkUserImportValidateStepPagination = (state: AppState) =>
    state.createUsers.bulkImport.validateStepPagination;

export const {
    setValidateFileStep,
    setTemplateData,
    setImportSettings,
    clearUsersBulkImport,

    setUserStatus,
    setUserType,
    setFile,
    setTargetAccount,
    setTemplateNewsletter,
    updateColumnByIds,
    updateColumnByRowIndexes,
    applyUpdatedData,
    setCurrentPage,
    setPageSize,
    removeRecords,
    setType,
    setStatus,
    setGroup,
    setCountry,
    setNewsletters,
} = userBulkImportSlice.actions;
